/* eslint-disable import/order, @typescript-eslint/no-require-imports */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'components/translate';
import { sanitize } from '@hotmart/devsec-toolkit';
import { FeatureToggleService, FeatureToggles } from 'modules/featureToggle';
import AdvertisementEditorService from 'modules/content/advertisementEditor/AdvertisementEditorService';
import FileUploadValidations from 'modules/file/components/FileUploadValidations';
import { UploadConsumer, UploadProptypes } from '@hotmart/club-files-ui';
import { ENTITY_TYPE, FILE_EXCEPTIONS } from 'modules/file/constants';

import { REGEX_DETECT_SCRIPT_TAG } from './constants';

import './redactor/redactor.min.js';
import './redactor/limiter.min.js';
import './redactor/clips.min.js';
import './redactor/fontfamily.min.js';
import './redactor/fontcolor.min.js';
import './redactor/fullscreen.min.js';
import './redactor/fontsize.min.js';
import './redactor/cropper.min.js';
import './redactor/upsell.js';

import './ContentEditor.scss';
import 'configs/styles/redactor-font.css';

import upsellImage from './redactor/upsell-placeholder.png';

import { UserService } from 'modules/user';
import { InputBlock } from 'components/input';
import { encodeEntities } from 'helpers/String';
import flowRight from 'lodash/flowRight';
import contentEditorUtils from './contentEditorUtils';

const PASTE_REDACTOR_DELAY = 100;
const FALLBACK_LANGUAGE = 'EN';

export class ContentEditor extends Component {
  static propTypes = {
    content: PropTypes.string,
    forwardRef: PropTypes.node,
    name: PropTypes.string,
    maxLength: PropTypes.string,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    plugins: PropTypes.array,
    t: PropTypes.func,
    upsell: PropTypes.bool,
    value: PropTypes.string,
    reduced: PropTypes.bool,
    setRedactorUploader: PropTypes.func.isRequired,
    fileUpload: UploadProptypes.fileUpload
  };

  static defaultProps = {
    plugins: ['clips', 'fontsize', 'fontfamily', 'fontcolor', 'fullscreen'],
    reduced: false,
    maxLength: '16383'
  };

  state = {
    errorUpload: {
      active: false
    }
  };

  constructor(props) {
    super(props);
    this.setLanguage();
  }

  setLanguage() {
    try {
      this.language = UserService.language.toUpperCase();
      require(`./languages/${this.language}.js`);
    } catch {
      this.language = FALLBACK_LANGUAGE;
      require(`./languages/${this.language}.js`);
    }
  }

  componentDidMount() {
    this.$el = $(this.editor);
    this.props.reduced ? this.initReducedRedactor() : this.initRedactor();
  }

  componentWillUnmount() {
    contentEditorUtils.removeRemainingElementsFromDOM();
  }

  get upsellConfig() {
    return {
      text: this.props.t('page_content.add_widget'),
      placeholder: upsellImage
    };
  }

  async upsellData() {
    return await AdvertisementEditorService.getUpsellWidgetData();
  }

  initReducedRedactor = () => {
    this.$el.redactor({
      replaceDivs: true,
      lang: this.language,
      buttons: ['format', 'bold', 'italic', 'link'],
      pastePlainText: true,
      pasteImages: false,
      limiter: this.props.maxLength,
      plugins: ['limiter'],
      maxHeight: 600,
      pasteBeforeCallback: (html) => {
        const rawHtml = new DOMParser().parseFromString(html, 'text/html');
        const safeHTML = this.sanitizeHTML(rawHtml.documentElement.textContent);

        return encodeEntities(safeHTML);
      },
      blurCallback: this.sanitizeEditorValue
    });

    this.bindRedactorChange();
  };

  initRedactor = async () => {
    const { setRedactorUploader, fileUpload, upsell } = this.props;
    let { plugins } = this.props;

    if (upsell) {
      plugins = plugins.concat('upsell');

      const upsellData = await this.upsellData();

      window.loadUpsellDropdown(this.upsellConfig, upsellData);
    }

    this.$el.redactor({
      fileUpload,
      replaceDivs: true,
      imageUpload: '/admin/rest/v1/upload/uploadImage',
      imageManagerJson: '/admin/rest/v1/upload/managerImage',
      imageUploadErrorCallback: this.showImageUploadErrorMessage.bind(this),
      pasteBeforeCallback: this.sanitizeHTML,
      uploadEntityType: ENTITY_TYPE.PAGE_CONTENT_IMG,
      plugins,
      lang: this.language,
      maxHeight: 600,
      setUploader: (redactorUploader) =>
        setRedactorUploader(redactorUploader, fileUpload)
    });

    this.bindRedactorChange();
  };

  bindRedactorChange = () => {
    const redactorSelector = '.redactor-editor';
    const redactorElement = this.$el.parent().find(redactorSelector);

    redactorElement.on('paste', () => {
      this.pasted = true;
    });

    redactorElement.on('keydown input paste', () => {
      let content = '';

      /* Redactor does a lot of processing when pasting a new text, this async behavior was
      leaving react state outdated. */
      setTimeout(() => {
        content = $(redactorSelector).html();
        this.onEditorChange(content);
      }, PASTE_REDACTOR_DELAY);
    });
  };

  showImageUploadErrorMessage(json, err) {
    const error = JSON.parse(err);

    if (error) {
      const maxFileSize = 2000000;
      const exception =
        FILE_EXCEPTIONS.BIG_FILE === error.name
          ? FILE_EXCEPTIONS.BIG_FILE
          : FILE_EXCEPTIONS.INVALID_EXTENSION;

      const errorUpload = {
        active: true,
        exception,
        maxFileSize
      };

      this.setState({ errorUpload });
    }
  }

  closeModalError() {
    const errorUpload = {
      active: false
    };

    $('#redactor-progress').remove();

    this.setState({ errorUpload });
  }

  normalizeHtmlTags(text) {
    const redactorTagOpen = '<pre><code data-redactor-tag="code">';
    const redactorTagClose = '</code></pre>';

    if (text.includes(redactorTagOpen) || text.includes(redactorTagClose)) {
      const redactorTagRegexOpen = new RegExp(redactorTagOpen, 'g');
      const redactorTagRegexClose = new RegExp(redactorTagClose, 'g');
      const escape = /\n/;

      return text
        .replace(redactorTagRegexOpen, '<code data-redactor-tag="code"><pre>')
        .replace(redactorTagRegexClose, '</pre></code>')
        .replace(escape, '');
    }

    return text;
  }

  sanitizeHTML = (html) => {
    if (FeatureToggleService.isEnabled(FeatureToggles.HtmlSanitizer)) {
      return sanitize({ html });
    }
    return html;
  };

  sanitizeEditorValue = () => {
    const { value } = this.props;
    const sanitized = this.sanitizeHTML(value);

    this.onEditorChange(sanitized);
  };

  onEditorChange = (value) => {
    const { name, onChange } = this.props;

    let text = this.normalizeHtmlTags(
      value.replace(REGEX_DETECT_SCRIPT_TAG, '')
    );

    if (this.pasted) {
      text = this.sanitizeHTML(text);
      this.pasted = false;
    }
    onChange && onChange({ target: { name, value: text, type: 'html' } });
  };

  onSourceCodeChange = (value) => {
    this.onEditorChange(value);
  };

  onPasted = () => {
    this.pasted = true;
  };

  render() {
    const { id, name, value, placeholder, t } = this.props;
    const { errorUpload } = this.state;

    return (
      <InputBlock {...this.props}>
        <div className="text-editor">
          <textarea
            className="redactor"
            id={id}
            name={name}
            onBlur={this.sanitizeEditorValue}
            onPaste={this.onPasted}
            onChange={(e) => this.onSourceCodeChange(e.target.value)}
            placeholder={placeholder && t(placeholder)}
            ref={(node) => (this.editor = node)}
            value={value}
          />
        </div>
        {errorUpload.active && (
          <FileUploadValidations
            resetException={this.closeModalError.bind(this)}
            onConfirm={this.closeModalError.bind(this)}
            {...errorUpload}
          />
        )}
      </InputBlock>
    );
  }
}

export default flowRight(
  UploadConsumer(ENTITY_TYPE.PAGE_CONTENT_IMG),
  translate()
)(ContentEditor);
